

import { FormikProps } from 'formik';
import ATMTextField from 'src/components/atoms/FormElements/ATMTextField/ATMTextField';
import MOLFormDialog from 'src/components/molecules/MOLFormDialog/MOLFormDialog';
import { PaymentMode } from '../models/OpenRegister.model';
import ATMCircularProgress from 'src/components/atoms/ATMCircularProgress/ATMCircularProgress';
import React, { useEffect, useRef, useState } from 'react';
import { IconCalendar, IconCreditCard } from '@tabler/icons-react';
import ATMDialog from 'src/components/atoms/ATMDialog/ATMDialog';
import ATMFileUploader from 'src/components/atoms/FormElements/ATMFileUploader/ATMFileUploader';
import { showToast } from 'src/utils/showToaster';
import { ATMButton } from 'src/components/atoms/ATMButton/ATMButton';
import ATMNumberField from 'src/components/atoms/FormElements/ATMNumberField/ATMNumberField';
import { eachDayOfInterval, format } from 'date-fns';

// types
type Props = {
  formikProps: FormikProps<PaymentMode>;
  onClose: () => void;
  formType: 'OPEN' | 'EDIT';
  isLoading?: boolean;
  opningData?: any;
  setShowSummary: any;
  showSummary: boolean;
};

const CloseRegisterFormLayout = ({
  formikProps,
  onClose,
  formType,
  isLoading = false,
  opningData,
  setShowSummary,
  showSummary
}: Props) => {
  const { values, setFieldValue, isSubmitting, handleBlur } = formikProps;
  const summaryRef = useRef<HTMLDivElement>(null);
  const formHeading = formType === 'OPEN' ? 'Close Register' : 'Edit Register';
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [activeRowId, setActiveRowId] = useState<string | null>(null);
  const [reasonText, setReasonText] = useState('');
  const [previewMode, setPreviewMode] = useState(false);
  const [showPaymentPrintModal, setShowPaymentPrintModal] = useState(false);
  const [selectedPaymentMode, setSelectedPaymentMode] = useState<string | null>(null);
  let updatedResult: any[] = [];
  let closedRegister: any;

  if (opningData) {
    const formattedExistingRegister = {
      _id: opningData?.existingRegister?._id,
      totalAmount: opningData?.register?.openingBalance,
      paymentModeName: 'Opening Balance',
      date: format(new Date(opningData?.register?.openedAt), "yyyy-MM-dd"),
      type: 'opening'
    };

    // Flatten result by date for frontend display
    const flattenedPayments = opningData?.result?.flatMap((entry: any) =>
      entry.payments.map((p: any) => ({
        ...p,
        date: entry.date,
        type: 'sale'
      }))
    );

    updatedResult = [formattedExistingRegister, ...(flattenedPayments || [])];
    closedRegister = opningData?.register;
  }

  function getDateWiseCashTotal(cashUsage: any[]) {
    return cashUsage.reduce((acc, usage) => {
      const dateKey = new Date(usage.createdAt).toISOString().split("T")[0];
      if (!acc[dateKey]) acc[dateKey] = 0;
      acc[dateKey] += usage.amount;
      return acc;
    }, {} as Record<string, number>);
  }

  let cashUsageTotals: any;
  if (closedRegister) {
    cashUsageTotals = getDateWiseCashTotal(closedRegister?.cashUsage);
  }

  const openingBalance = updatedResult?.[0]?.totalAmount || 0;

  // Get all cash sale rows (excluding opening balance)
  const cashSaleRows = updatedResult.filter(
    (item) => item.paymentModeName?.toLowerCase() === 'cash' && item.type === 'sale'
  );

  // Calculate total cash from sales
  const totalCashSales = cashSaleRows.reduce(
    (sum, row) => sum + (parseFloat(row.totalAmount) || 0), 0
  );

  // Calculate total cash payouts
  const totalCashPayouts = closedRegister?.cashUsage?.reduce((sum: number, p: any) => {
    if ((p.paymentMode || '').toLowerCase() === 'cash') {
      return sum + (parseFloat(p.amount) || 0);
    }
    return sum;
  }, 0) || 0;

  // Final cash total = Opening Balance + Total Cash Sales - Total Cash Payouts
  const cashTotal = openingBalance + totalCashSales - totalCashPayouts;

  // Find the last cash row (latest date)
  const groupedResult = updatedResult.slice(1).reduce((acc: Record<string, any[]>, row: any) => {
    if (!row?.date) return acc;
    acc[row.date] = acc[row.date] || [];
    acc[row.date].push(row);
    return acc;
  }, {});

  // Get all rows with flatIndex
  const allRows = Object.entries(groupedResult)
    .flatMap(([date, rows]) =>
      rows.map((row) => ({ ...row, date, flatIndex: `${date}_${row._id}` }))
    );

  // Filter cash rows and sort by date
  const cashRows = allRows
    .filter((row) => row.paymentModeName?.toLowerCase() === "cash")
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  // Get last cash row
  const lastCashRow = cashRows[cashRows.length - 1] || null;
  const lastCashRowKey = lastCashRow ? `${lastCashRow.date}_${lastCashRow._id}` : '';

  // 🛠 Utility: Fill missing dates with zero rows
  function fillMissingDates(groupedResult: any, startDate: string, endDate: string) {
    const allDates = eachDayOfInterval({
      start: new Date(startDate),
      end: new Date(endDate),
    });

    const filledResult: any = {};

    allDates.forEach((date) => {
      const dateKey = format(date, "yyyy-MM-dd");
      if (groupedResult[dateKey]) {
        filledResult[dateKey] = groupedResult[dateKey];
      } else {
        // For missing dates, create empty structure
        filledResult[dateKey] = [
          {
            _id: "no-sale",
            paymentModeName: "No Sales",
            totalAmount: 0,
            date: dateKey,
            type: 'empty'
          }
        ];
      }
    });

    return filledResult;
  }

  const registerStartDate = opningData?.register?.openedAt;
  const today = new Date();
  const todayStr = format(today, "yyyy-MM-dd");

  // Fill missing dates for complete view
  const completeResult = fillMissingDates(groupedResult, registerStartDate, todayStr);

  // Check if submit should be disabled
  const isSubmitDisabled =
    !opningData?.result?.length ||
    closedRegister?.isClosed === true ||
    !closedRegister?.isOpened;

  const handlePrint = () => {
    if (!summaryRef.current) return;

    const content = summaryRef.current.innerHTML;
    const printWindow = window.open('', '_blank');

    if (printWindow) {
      printWindow.document.open();
      printWindow.document.write(`
      <html>
        <head>
          <title>Register Summary</title>
          <style>
            @page {
              size: A4;
              margin: 20mm;
            }
            html, body {
              font-family: Arial, sans-serif;
              padding: 0;
              margin: 0;
              background: white;
              color: #000;
            }
            body {
              width: 210mm;
              min-height: 297mm;
              padding: 20mm;
              box-sizing: border-box;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 1rem;
              font-size: 12px;
            }
            th, td {
              border: 1px solid #999;
              padding: 6px;
              text-align: left;
            }
            h2, h3, h4 {
              margin-top: 1rem;
            }
            a, button {
              display: none !important;
            }
            ul {
              padding-left: 20px;
            }
            li {
              margin-bottom: 6px;
            }
          </style>
        </head>
        <body>
          ${content}
        </body>
      </html>
    `);
      printWindow.document.close();

      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      };
    }
  };

  const carryForwardBalance = (() => {
    const deposit = parseFloat(values.bankDeposit) || 0;
    const carryForward = Math.max(cashTotal - deposit, 0);
    return carryForward.toFixed(2);
  })()

  // Filter data by payment mode for printing
  const getPaymentModeData = (paymentMode: string) => {
    const filteredRows = updatedResult.filter(
      (item) => item.paymentModeName?.toLowerCase() === paymentMode.toLowerCase() &&
        item.type === 'sale'
    );

    // Group by date
    const grouped = filteredRows.reduce((acc: Record<string, any[]>, row: any) => {
      if (!row?.date) return acc;
      acc[row.date] = acc[row.date] || [];
      acc[row.date].push(row);
      return acc;
    }, {});

    return grouped;
  };

  // Calculate total for a payment mode
  const getPaymentModeTotal = (paymentMode: string) => {
    return updatedResult
      .filter((item) => item.paymentModeName?.toLowerCase() === paymentMode.toLowerCase() &&
        item.type === 'sale')
      .reduce((sum, row) => sum + (parseFloat(row.totalAmount) || 0), 0);
  };

  // Handle print for specific payment mode
  const handlePaymentModePrint = (paymentMode: string) => {
    const paymentData = getPaymentModeData(paymentMode);
    const total = getPaymentModeTotal(paymentMode);

    const printContent = `
      <html>
        <head>
          <title>${paymentMode} Summary</title>
          <style>
            @page {
              size: A4;
              margin: 20mm;
            }
            html, body {
              font-family: Arial, sans-serif;
              padding: 0;
              margin: 0;
              background: white;
              color: #000;
            }
            body {
              width: 210mm;
              min-height: 297mm;
              padding: 20mm;
              box-sizing: border-box;
            }
            h1 {
              color: #333;
              border-bottom: 2px solid #333;
              padding-bottom: 10px;
            }
            h2 {
              color: #666;
              margin-top: 20px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 1rem;
              font-size: 12px;
            }
            th, td {
              border: 1px solid #999;
              padding: 8px;
              text-align: left;
            }
            th {
              background-color: #f0f0f0;
              font-weight: bold;
            }
            .total {
              font-size: 16px;
              font-weight: bold;
              text-align: right;
              margin-top: 20px;
              padding: 10px;
              background-color: #f0f0f0;
            }
            .header-info {
              margin-bottom: 20px;
              padding: 10px;
              background-color: #f9f9f9;
              border: 1px solid #ddd;
            }
          </style>
        </head>
        <body>
          <h1>${paymentMode} Payment Summary</h1>
          
          <div class="header-info">
            <p><strong>Outlet:</strong> ${opningData?.register?.outletId?.name || '-'}</p>
            <p><strong>Date Range:</strong> ${format(new Date(registerStartDate), 'dd/MM/yyyy')} to ${format(new Date(), 'dd/MM/yyyy')}</p>
            <p><strong>Generated On:</strong> ${format(new Date(), 'dd/MM/yyyy HH:mm:ss')}</p>
          </div>

          ${Object.entries(paymentData)
        .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime())
        .map(([date, rows]) => `
              <h2>${format(new Date(date), 'EEEE, dd MMMM yyyy')}</h2>
              <table>
                <thead>
                  <tr>
                    <th>Payment Mode</th>
                    <th>Amount (R)</th>
                    <th>Reference/Notes</th>
                  </tr>
                </thead>
                <tbody>
                  ${rows.map((row: any) => `
                    <tr>
                      <td>${row.paymentModeName}</td>
                      <td>${parseFloat(row.totalAmount).toFixed(2)}</td>
                      <td>${row.reference || row.notes || '-'}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            `).join('')}

          <div class="total">
            Total ${paymentMode} Payments: R ${total.toFixed(2)}
          </div>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.open();
      printWindow.document.write(printContent);
      printWindow.document.close();

      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      };
    }
  };

  // Get unique payment modes (excluding cash, opening balance, and no-sale)
  const getUniquePaymentModes = () => {
    const modes = new Set<string>();
    updatedResult.forEach((item) => {
      if (item.paymentModeName &&
        item.paymentModeName.toLowerCase() !== 'cash' &&
        item.paymentModeName.toLowerCase() !== 'opening balance' &&
        item.type === 'sale') {
        modes.add(item.paymentModeName);
      }
    });
    return Array.from(modes);
  };

  const uniquePaymentModes = getUniquePaymentModes();

  return (
    <MOLFormDialog
      title={formHeading}
      onClose={onClose}
      isSubmitting={isSubmitting}
      isSubmitButtonDisabled={isSubmitDisabled}
    >
      {isLoading ? (
        <div className="flex justify-center items-center max-w-[500px] h-[140px]">
          <ATMCircularProgress />
        </div>
      ) : !closedRegister?.isOpened ? (
        <div className="text-center text-yellow-600 font-semibold text-lg py-6">
          Please open register first.
        </div>
      ) : closedRegister?.isClosed === true ? (
        <div className="text-center text-red-600 font-semibold text-lg py-6">
          This register is already closed.
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex justify-center items-center">
            <div className="rounded-2xl shadow-xl w-full max-w-5xl bg-white border border-gray-200">
              <table className="w-full border-collapse rounded-2xl overflow-hidden">
                <thead className="bg-gray-100 text-sm text-gray-800 uppercase tracking-wide overflow-auto">
                  <tr>
                    <th className="p-3 border">Payment Type</th>
                    <th className="p-3 border">Opening Register</th>
                    <th className="p-3 border">Close Register (Automatic)</th>
                    <th className="p-3 border">Manual Calculation</th>
                  </tr>
                </thead>

                <tbody className="text-sm text-gray-700 font-medium">
                  {/* Opening Balance Row */}
                  {updatedResult.length > 0 && (
                    <tr className="border hover:bg-gray-50 transition-all">
                      <td className="p-3 border font-semibold">Opening Balance</td>
                      <td className="p-3 border text-green-600 font-semibold text-center">
                        R {openingBalance?.toFixed(2)}
                      </td>
                      <td className="p-3 border"></td>
                      <td className="p-3 border"></td>
                    </tr>
                  )}

                  {/* Group by date */}
                  {Object.entries(groupedResult)
                    .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime())
                    .map(([date, rows]) => (
                      <React.Fragment key={date}>
                        {/* Date Row */}
                        <tr className="bg-gray-100 text-sm text-blue-700">
                          <td colSpan={4} className="p-2 font-bold">
                            <div className="flex items-center gap-2">
                              <IconCalendar size={20} /> {new Date(date).toDateString()}
                            </div>
                          </td>
                        </tr>

                        {rows.map((row, index) => {
                          const uniqueKey = `${date}_${row._id}`;
                          const isCashRow = row.paymentModeName?.toLowerCase() === 'cash';
                          const isLastCashRow = isCashRow && uniqueKey === lastCashRowKey;

                          return (
                            <tr key={uniqueKey} className="border hover:bg-gray-50 transition-all">
                              <td className="p-3 border capitalize">
                                {row.paymentModeName} Sale
                              </td>
                              <td className="p-3 border text-center">-</td>
                              <td className="p-3 border text-blue-600 font-semibold text-center">
                                R {row?.totalAmount.toFixed(2)}
                              </td>
                              <td className="p-3 border">
                                {/* Show input for all rows except non-last cash rows */}
                                {(!isCashRow || isLastCashRow) && (
                                  <ATMTextField
                                    name={`manual.${uniqueKey}`}
                                    value={values.manual?.[uniqueKey] || ''}
                                    onChange={(e) => setFieldValue(`manual.${uniqueKey}`, e.target.value)}
                                    onBlur={handleBlur}
                                    className="w-full p-2 border border-gray-300 rounded-md text-sm"
                                    placeholder="Enter amount"
                                  />
                                )}

                                {values.manual?.[uniqueKey] && (
                                  <div className="text-xs font-medium">
                                    {isCashRow ? (
                                      isLastCashRow ? (
                                        parseFloat(values.manual[uniqueKey]) === cashTotal ? (
                                          <span className="text-green-600">✅ Success</span>
                                        ) : (
                                          <span
                                            onClick={() => {
                                              setActiveRowId(uniqueKey);
                                              setShowReasonModal(true);
                                              setReasonText(values.reasons?.[uniqueKey] || '');
                                            }}
                                            className={`cursor-pointer underline ${parseFloat(values.manual[uniqueKey]) > cashTotal
                                              ? 'text-red-600'
                                              : 'text-orange-600'
                                              }`}
                                          >
                                            {parseFloat(values.manual[uniqueKey]) > cashTotal
                                              ? '⬆ Greater'
                                              : '⬇ Less'}{' '}
                                            – Add Reason
                                          </span>
                                        )
                                      ) : null
                                    ) : (
                                      parseFloat(values.manual[uniqueKey]) === row.totalAmount ? (
                                        <span className="text-green-600">✅ Success</span>
                                      ) : (
                                        <span
                                          onClick={() => {
                                            setActiveRowId(uniqueKey);
                                            setShowReasonModal(true);
                                            setReasonText(values.reasons?.[uniqueKey] || '');
                                          }}
                                          className={`cursor-pointer underline ${parseFloat(values.manual[uniqueKey]) > row.totalAmount
                                            ? 'text-red-600'
                                            : 'text-orange-600'
                                            }`}
                                        >
                                          {parseFloat(values.manual[uniqueKey]) > row.totalAmount
                                            ? '⬆ Greater'
                                            : '⬇ Less'}{' '}
                                          – Add Reason
                                        </span>
                                      )
                                    )}
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}

                        {/* Show Payout rows for this date */}
                        {cashUsageTotals && cashUsageTotals[date] && (
                          <tr key={`payout_${date}`}>
                            <td className="p-3 border">Payout (Cash)</td>
                            <td className="p-3 border text-center">-</td>
                            <td className="p-3 border text-blue-600 font-semibold text-center">
                              R {cashUsageTotals[date].toFixed(2)}
                            </td>
                            <td className="p-3 border">
                              <ATMTextField
                                name={`manual.payout_${date}`}
                                value={values.manual?.[`payout_${date}`] || ''}
                                onChange={(e) =>
                                  setFieldValue(`manual.payout_${date}`, e.target.value)
                                }
                                onBlur={handleBlur}
                                className="w-full p-2 border border-gray-300 rounded-md text-sm"
                                placeholder="Enter amount"
                              />

                              {values.manual?.[`payout_${date}`] && (
                                <div className="text-xs font-medium">
                                  {parseFloat(values.manual[`payout_${date}`]) === cashUsageTotals[date] ? (
                                    <span className="text-green-600">✅ Success</span>
                                  ) : (
                                    <span
                                      onClick={() => {
                                        setActiveRowId(`payout_${date}`);
                                        setShowReasonModal(true);
                                        setReasonText(values.reasons?.[`payout_${date}`] || '');
                                      }}
                                      className={`cursor-pointer underline ${parseFloat(values.manual[`payout_${date}`]) > cashUsageTotals[date]
                                        ? 'text-red-600'
                                        : 'text-orange-600'
                                        }`}
                                    >
                                      {parseFloat(values.manual[`payout_${date}`]) > cashUsageTotals[date]
                                        ? '⬆ Greater'
                                        : '⬇ Less'}{' '}
                                      – Add Reason
                                    </span>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                </tbody>
              </table>

              {/* Total Cash Display - Always visible at bottom */}
              <div className="mt-4 p-2 bg-blue-50 border-t-2 border-blue-200">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold text-blue-800">Total Cash in Counter:</span>
                  <span className="text-2xl font-extrabold text-blue-600">R {cashTotal.toFixed(2)}</span>
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  Opening Balance: R {openingBalance.toFixed(2)} +
                  Total Sales: R {totalCashSales.toFixed(2)} -
                  Total Payouts: R {totalCashPayouts.toFixed(2)}
                </div>
              </div>

              {/* Bank Deposit Section */}
              <div className="mt-6 p-4 border-t">
                <label className="block mb-2 font-semibold text-gray-800 text-base">
                  Enter Cash to Deposit in Bank
                </label>

                <div className="grid grid-cols-3 gap-4 items-center">
                  <div className='col-span-2'>
                    <ATMTextField
                      name="bankDeposit"
                      value={values.bankDeposit}
                      onChange={(e) => {
                        const input = e.target.value;
                        const num = parseFloat(input);
                        if (input === '' || (!isNaN(num) && num <= cashTotal)) {
                          setFieldValue('bankDeposit', input);
                        }
                      }}
                      onBlur={handleBlur}
                      placeholder="Enter amount"
                      className="w-full p-2 border border-gray-300 rounded-md text-sm"
                    />
                  </div>
                </div>

                {values.bankDeposit !== '' &&
                  parseFloat(values.bankDeposit) > cashTotal && (
                    <div className="text-red-600 text-sm mt-2">
                      You cannot deposit more than R {cashTotal.toFixed(2)}
                    </div>
                  )}

                {values.bankDeposit > 0 && (
                  <div className="mt-3 text-sm text-blue-700 font-semibold">
                    Carry Forward to Next Day Opening Balance: R{' '}
                    {(() => {
                      const deposit = parseFloat(values.bankDeposit) || 0;
                      const carryForward = Math.max(cashTotal - deposit, 0);
                      return carryForward.toFixed(2);
                    })()}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reason Modal */}
      {showReasonModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-md w-full max-w-sm p-4">
            <h3 className="text-lg font-semibold mb-3">Reason Required</h3>
            <textarea
              value={reasonText}
              onChange={(e) => setReasonText(e.target.value)}
              placeholder="Enter reason for discrepancy"
              className="w-full border border-gray-300 p-2 rounded h-24 resize-none"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => {
                  setShowReasonModal(false);
                  setReasonText('');
                  setActiveRowId(null);
                }}
                className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (!reasonText.trim()) {
                    alert('Reason is required');
                    return;
                  }
                  setFieldValue(`reasons.${activeRowId}`, reasonText);
                  setShowReasonModal(false);
                  setReasonText('');
                  setActiveRowId(null);
                }}
                className="bg-primary-60 hover:bg-primary-70 text-white px-4 py-1 rounded"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Summary Modal */}
      {showSummary && (
        <div ref={summaryRef} className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
          <div className="bg-white rounded-xl p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto space-y-6 shadow-lg">
            <h2 className="text-xl font-bold text-center mt-4">Register Closure Summary</h2>

            <div className="space-y-4 text-sm text-gray-800 overflow-auto">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="space-y-1">
                  <p><strong>Open Date</strong>: {new Date(opningData?.register?.openedAt).toLocaleDateString()}</p>
                  <p><strong>Close Date</strong>: {new Date().toLocaleDateString()}</p>
                </div>
                <div className="space-y-1">
                  <p><strong>Outlet</strong>: {opningData?.register?.outletId?.name}</p>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
                <p><strong>Opening Balance</strong>: R {openingBalance?.toFixed(2) || '0.00'}</p>
                <p><strong>Bank Deposit</strong>: R {formikProps.values.bankDeposit || '0.00'}</p>
                <p><strong>Carry Forward</strong>: R {carryForwardBalance}</p>
              </div>

              {Object.keys(completeResult)
                .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
                .map((date) => (
                  <div key={date}>
                    <h3 className="text-base font-semibold text-primary-60 mb-2">
                      {new Date(date).toDateString()}
                    </h3>

                    <table className="w-full border text-sm mb-4">
                      <thead className="bg-gray-100 text-gray-700">
                        <tr>
                          <th className="p-2 border text-left">Payment Mode</th>
                          <th className="p-2 border text-right">Expected</th>
                          <th className="p-2 border text-right">Counted</th>
                          <th className="p-2 border text-right">Difference</th>
                          <th className="p-2 border text-left">Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {completeResult[date].map((row: any) => {
                          if (row.type === 'empty') return null;

                          const rowKey = `${date}_${row._id}`;
                          const manual = formikProps.values.manual?.[rowKey];
                          const reason = formikProps.values.reasons?.[rowKey];

                          let expected = parseFloat(row.totalAmount || 0);

                          // For cash rows, use cashTotal for the last cash row
                          const isLastCashRow = row.paymentModeName?.toLowerCase() === 'cash' &&
                            `${date}_${row._id}` === lastCashRowKey;

                          if (isLastCashRow) {
                            expected = cashTotal;
                          }

                          const manualNum = parseFloat(manual || "0");
                          const isMismatch = manual && manualNum !== expected;
                          const difference = manual ? manualNum - expected : 0;

                          return (
                            <tr key={rowKey} className={isMismatch ? "bg-red-50" : ""}>
                              <td className="p-2 border capitalize">{row.paymentModeName} Sale</td>
                              <td className="p-2 border text-right">R {expected.toFixed(2)}</td>
                              <td className="p-2 border text-right">
                                {manual ? `R ${manual}` : "-"}
                              </td>
                              <td className="p-2 border text-right">
                                {manual ? `R ${difference.toFixed(2)}` : "-"}
                              </td>
                              <td className="p-2 border">
                                {isMismatch && reason ? (
                                  <span className="text-orange-600">{reason}</span>
                                ) : "-"}
                              </td>
                            </tr>
                          );
                        })}

                        {/* Add payout row for this date if exists */}
                        {cashUsageTotals && cashUsageTotals[date] && (
                          <tr>
                            <td className="p-2 border">Payout (Cash)</td>
                            <td className="p-2 border text-right">R {cashUsageTotals[date].toFixed(2)}</td>
                            <td className="p-2 border text-right">
                              {formikProps.values.manual?.[`payout_${date}`]
                                ? `R ${formikProps.values.manual[`payout_${date}`]}`
                                : "-"}
                            </td>
                            <td className="p-2 border text-right">
                              {formikProps.values.manual?.[`payout_${date}`]
                                ? `R ${(parseFloat(formikProps.values.manual[`payout_${date}`]) - cashUsageTotals[date]).toFixed(2)}`
                                : "-"}
                            </td>
                            <td className="p-2 border">
                              {formikProps.values.reasons?.[`payout_${date}`] || "-"}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                ))}

              <div>
                <h4 className='my-2 font-bold'>Cash Movement</h4>
                <table className="w-full border text-sm mb-4">
                  <thead className="bg-gray-100 text-gray-700">
                    <tr>
                      <th className="p-2 border text-left">Type</th>
                      <th className="p-2 border text-left">Date and Time</th>
                      <th className="p-2 border text-left">User</th>
                      <th className="p-2 border text-left">Note</th>
                      <th className="p-2 border text-left">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="p-2 border">Opening Float</td>
                      <td className="p-2 border">
                        {new Date(opningData?.register?.createdAt).toLocaleDateString()}{" "}
                        {new Date(opningData?.register?.createdAt).toLocaleTimeString()}
                      </td>
                      <td className="p-2 border">
                        {opningData?.register?.userName || "-"}
                      </td>
                      <td className="p-2 border">-</td>
                      <td className="p-2 border">
                        R {openingBalance?.toFixed(2)}
                      </td>
                    </tr>

                    {closedRegister?.cashUsage?.map((item: any, index: any) => (
                      <tr key={index}>
                        <td className="p-2 border">Cash Out ({item?.paymentMode})</td>
                        <td className="p-2 border">
                          {new Date(item.createdAt).toLocaleDateString()}{" "}
                          {new Date(item.createdAt).toLocaleTimeString()}
                        </td>
                        <td className="p-2 border">
                          {item.customerName || "-"}
                        </td>
                        <td className="p-2 border">
                          {item.reason || "-"}
                        </td>
                        <td className="p-2 border">
                          R {item.amount?.toFixed(2)}
                        </td>
                      </tr>
                    ))}

                    <tr>
                      <td className="p-2 border">Closing Float</td>
                      <td className="p-2 border">
                        {new Date().toLocaleDateString()}{" "}
                        {new Date().toLocaleTimeString()}
                      </td>
                      <td className="p-2 border">
                        {closedRegister?.closedBy || "-"}
                      </td>
                      <td className="p-2 border">-</td>
                      <td className={`p-2 border ${(cashTotal - (parseFloat(values.bankDeposit) || 0)) < 0 ? "text-red-500" : ""}`}>
                        R {Math.max(cashTotal - (parseFloat(values.bankDeposit) || 0), 0).toFixed(2)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="space-y-2">
              {uniquePaymentModes.map((mode) => (
                <button
                  key={mode}
                  onClick={() => {
                    handlePaymentModePrint(mode);
                    setShowPaymentPrintModal(false);
                    setSelectedPaymentMode(null);
                  }}
                  className="w-full px-3 py-2 border rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-between"
                >
                  {/* Left Side */}
                  <div className="flex items-center gap-2">
                    <IconCreditCard size={16} className="text-gray-600" />
                    <span className="text-sm font-medium">{mode?.charAt(0).toUpperCase() + mode.slice(1)} <span className='text-xs text-gray-500'>click to print</span></span>
                  </div>

                  {/* Amount */}
                  <span className="text-sm font-semibold text-gray-700">
                    R {getPaymentModeTotal(mode).toFixed(2)}
                  </span>
                </button>
              ))}

              {uniquePaymentModes.length === 0 && (
                <p className="text-gray-500 text-center py-4 text-sm">
                  No non-cash payment modes found
                </p>
              )}
            </div>

            <div className="flex justify-end gap-4 pt-4 border-t">
              <ATMButton variant='outlined' onClick={() => setShowSummary(false)}>
                Back
              </ATMButton>
              <ATMButton variant='outlined' onClick={handlePrint}>
                Print
              </ATMButton>
              <ATMButton onClick={() => {
                setShowSummary(false);
                formikProps.submitForm();
              }}>
                Confirm & Submit
              </ATMButton>
            </div>
          </div>
        </div>
      )}
    </MOLFormDialog>
  );
};

export default CloseRegisterFormLayout;
